import { generateId } from "@common/utils/common";
import { QuestionnaireTextBlockLocalDto } from "@modules/fhq-design-tool/dto/text-block-create-dto";
import React from "react";
import { I18n } from "react-redux-i18n";

type TextBlockProps<Page extends { texts?: Array<QuestionnaireTextBlockLocalDto> }> = {
  data?: Page;
  onChange: (data: Page) => void;
};

export const useTextBlock = <Page extends { texts?: Array<QuestionnaireTextBlockLocalDto> }>(props: TextBlockProps<Page>) => {
  const { data, onChange } = props;
  const [editingTextBlockId, setEditingTextBlockId] = React.useState<QuestionnaireTextBlockLocalDto['id']>();

  const addTextBlock = React.useCallback((params?: { startOrder?: number }) => {
    if (!data) {
      return;
    }

    const newTextBlock: QuestionnaireTextBlockLocalDto = {
      id: generateId(),
      title: I18n.t('Text'),
      text: '',
      order: params?.startOrder ?? (data?.texts?.length || 0) + 1,
      skipLogicEnabled: false,
      skipLogic: [],
      isNotCreated: true,
    };

    onChange({
      ...data,
      texts: [
        ...(data.texts || []),
        newTextBlock,
      ],
    });
  }, [data, onChange]);

  const handleTextBlockChange = React.useCallback((textBlock: QuestionnaireTextBlockLocalDto) => {
    if (!data) {
      return;
    }

    onChange({
      ...data,
      texts: data.texts?.map((x) => x.id === textBlock.id ? textBlock : x) || [textBlock],
    });
  }, [data, onChange]);

  const handleTextBlockDelete = React.useCallback((textBlock: QuestionnaireTextBlockLocalDto) => {
    if (!data) {
      return;
    }

    onChange({
      ...data,
      texts: data.texts?.filter((x) => x.id !== textBlock.id) || [],
    });
  }, [data, onChange]);

  const handleOrderChange = React.useCallback((newTextBlocksOrder: Array<QuestionnaireTextBlockLocalDto>) => {
    if (!data) {
      return;
    }

    onChange({
      ...data,
      texts: newTextBlocksOrder.map((x, i) => ({
        ...x,
        order: i + 1,
      })),
    });
  }, [data, onChange]);

  const openTextBlockEditing = React.useCallback((textBlock: QuestionnaireTextBlockLocalDto) => {
    setEditingTextBlockId(textBlock.id);
  }, []);

  const closeTextBlockEditing = React.useCallback(() => {
    setEditingTextBlockId(undefined);
  }, []);

  const editingTextBlock = data?.texts?.find((x) => x.id === editingTextBlockId);

  return {
    editingTextBlock,
    addTextBlock,
    handleOrderChange,
    handleTextBlockDelete,
    openTextBlockEditing,
    closeTextBlockEditing,
    handleTextBlockChange,
  };
};